import { useState, useMemo } from 'react'
import { Download, FileText, FileSpreadsheet, Calendar, Filter, Shield } from 'lucide-react'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, formatDate, formatTime, formatDuration } from '../hooks/useSupabase'
import { supabase } from '../lib/supabase'
import { useEffect } from 'react'
import { Button, Card, Select, PageHeader, Toast, StatCard, Badge } from '../components/ui/SharedUI'

/**
 * Date range presets
 */
// Confidence tiers for backfilled shifts (matches Backfill + migration 00006)
const CONFIDENCE_CSV_LABEL = {
  documented: 'Documented',
  pattern: 'Pattern-based',
  recollection: 'From memory',
}
const CONFIDENCE_PDF = {
  documented: { label: 'Documented', rgb: [0, 84, 64] },
  pattern: { label: 'Pattern-based', rgb: [44, 122, 123] },
  recollection: { label: 'From memory', rgb: [139, 80, 0] },
}

const RANGE_PRESETS = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'this_year', label: 'This year' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
]

function getRangeDates(preset, customStart, customEnd) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)

  switch (preset) {
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      return { start, end: today }
    }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
      return { start, end }
    }
    case 'last_3_months': {
      const start = new Date(now.getFullYear(), now.getMonth() - 3, 1)
      return { start, end: today }
    }
    case 'this_year': {
      const start = new Date(now.getFullYear(), 0, 1)
      return { start, end: today }
    }
    case 'all_time': {
      return { start: new Date(2020, 0, 1), end: today }
    }
    case 'custom': {
      const start = customStart ? new Date(customStart + 'T00:00:00') : new Date(now.getFullYear(), now.getMonth(), 1)
      const end = customEnd ? new Date(customEnd + 'T23:59:59') : today
      return { start, end }
    }
    default:
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: today }
  }
}

export function ExportPage() {
  const { user, profile } = useAuth()
  const { data: shifts } = useSupabaseQuery('shifts', { orderBy: 'clock_in', ascending: false })
  const { data: notes } = useSupabaseQuery('evidence_notes', { orderBy: 'created_at', ascending: false })
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id', orderBy: 'name', ascending: true })

  const [rangePreset, setRangePreset] = useState('this_month')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [recipientFilter, setRecipientFilter] = useState('')
  const [rates, setRates] = useState(null)
  const [corroborations, setCorroborations] = useState({})
  const [toast, setToast] = useState(null)
  const [generating, setGenerating] = useState(false)

  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r]))

  // Load rates + corroborations
  useEffect(() => {
    if (!user) return
    async function load() {
      const { data: ratesData } = await supabase.from('rates').select('*').eq('caregiver_id', user.id).single()
      setRates(ratesData)

      if (shifts.length > 0) {
        const shiftIds = shifts.map(s => s.id)
        const { data: corrobData } = await supabase
          .from('corroborations')
          .select('*')
          .in('shift_id', shiftIds)
          .order('created_at', { ascending: false })

        const grouped = {}
        for (const c of (corrobData || [])) {
          if (!grouped[c.shift_id]) grouped[c.shift_id] = []
          grouped[c.shift_id].push(c)
        }
        setCorroborations(grouped)
      }
    }
    load()
  }, [user, shifts.length])

  const { start, end } = getRangeDates(rangePreset, customStart, customEnd)

  // Filter data by range and recipient
  const filteredShifts = useMemo(() => {
    return shifts.filter(s => {
      if (s.is_active || !s.clock_out) return false
      const shiftDate = new Date(s.clock_in)
      if (shiftDate < start || shiftDate > end) return false
      if (recipientFilter && s.recipient_id !== recipientFilter) return false
      return true
    })
  }, [shifts, start, end, recipientFilter])

  const filteredNotes = useMemo(() => {
    return notes.filter(n => {
      const noteDate = new Date(n.date + 'T00:00:00')
      return noteDate >= start && noteDate <= end
    })
  }, [notes, start, end])

  // Calculate summary stats
  const summary = useMemo(() => {
    const loggedShifts = filteredShifts.filter(s => s.authorship !== 'observed')
    const observedShifts = filteredShifts.filter(s => s.authorship === 'observed')

    const totalHours = loggedShifts.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
    const observedHours = observedShifts.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
    const directCare = loggedShifts.filter(s => s.shift_type === 'direct_care')
    const directCareHours = directCare.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
    const noShows = loggedShifts.filter(s => s.shift_type === 'no_show_covered').length
    const incidents = filteredShifts.filter(s => s.incident).length

    // Calculate earnings — LOGGED only, never observed
    let earnings = 0
    if (rates) {
      for (const s of loggedShifts) {
        const hours = (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000
        const clientRate = rates.by_client?.[s.recipient_id]
        const rate = clientRate?.total_rate || rates.single_rate || 0
        earnings += hours * rate
      }
    }

    // Confidence breakdown across all backfilled shifts in range (honest strength picture)
    const backfilled = filteredShifts.filter(s => s.is_backfilled)
    const confidenceBreakdown = {
      documented: backfilled.filter(s => s.confidence === 'documented').length,
      pattern: backfilled.filter(s => s.confidence === 'pattern').length,
      recollection: backfilled.filter(s => s.confidence === 'recollection' || !s.confidence).length,
      total: backfilled.length,
    }
    const realtimeCount = filteredShifts.length - backfilled.length

    return {
      totalShifts: loggedShifts.length,
      totalHours,
      directCareHours,
      noShows,
      incidents,
      earnings,
      notesCount: filteredNotes.length,
      observedShifts: observedShifts.length,
      observedHours,
      confidenceBreakdown,
      realtimeCount,
    }
  }, [filteredShifts, filteredNotes, rates])

  // ═══════════════════════════════════════════════════════
  // CSV EXPORTS
  // ═══════════════════════════════════════════════════════

  function downloadCSV(filename, headers, rows) {
    const escape = (cell) => {
      if (cell === null || cell === undefined) return ''
      const str = String(cell)
      // Escape quotes and wrap if contains comma/quote/newline
      if (str.includes('"') || str.includes(',') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    }
    const csv = [
      headers.map(escape).join(','),
      ...rows.map(row => row.map(escape).join(','))
    ].join('\n')

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  function exportShiftsCSV() {
    const headers = [
      'Date', 'Day of week', 'Clock in', 'Clock out', 'Duration (hours)',
      'Authorship', 'Observed caregiver', 'Evidence source', 'Evidence source note',
      'Shift type', 'Care recipient', 'Tasks performed', 'Notes',
      'Incident', 'Backfilled', 'Confidence', 'Backfilled at', 'Locked (tamper-evident)',
      'Corroboration status', 'Corroborator', 'Corroboration date'
    ]
    const rows = filteredShifts.map(s => {
      const hours = ((new Date(s.clock_out) - new Date(s.clock_in)) / 3600000).toFixed(2)
      const dayOfWeek = new Date(s.clock_in).toLocaleDateString('en-US', { weekday: 'long' })
      const corrob = corroborations[s.id]?.[0]
      return [
        s.date,
        dayOfWeek,
        new Date(s.clock_in).toLocaleString(),
        new Date(s.clock_out).toLocaleString(),
        hours,
        s.authorship === 'observed' ? 'Observed' : 'Logged',
        s.observed_caregiver_name || '',
        s.evidence_source ? s.evidence_source.replace(/_/g, ' ') : '',
        s.evidence_source_note || '',
        s.shift_type.replace(/_/g, ' '),
        s.recipient_id ? recipientMap[s.recipient_id]?.name || '' : '',
        (s.tasks || []).map(t => t.replace(/_/g, ' ')).join('; '),
        s.notes || '',
        s.incident ? 'YES' : '',
        s.is_backfilled ? 'YES' : 'NO',
        s.is_backfilled ? (CONFIDENCE_CSV_LABEL[s.confidence] || 'From memory') : '',
        s.backfilled_at ? new Date(s.backfilled_at).toLocaleString() : '',
        s.locked_at ? 'YES' : 'NO',
        corrob?.state || '',
        corrob?.corroborator_name || '',
        corrob ? new Date(corrob.responded_at).toLocaleString() : '',
      ]
    })

    const filename = `kindredcare-shifts-${new Date().toISOString().split('T')[0]}.csv`
    downloadCSV(filename, headers, rows)
    setToast({ message: `${filteredShifts.length} shifts exported as CSV`, type: 'success' })
  }

  function exportNotesCSV() {
    const headers = ['Date', 'Timestamp created', 'Linked shift date', 'Note text']
    const rows = filteredNotes.map(n => {
      const linkedShift = n.shift_id ? shifts.find(s => s.id === n.shift_id) : null
      return [
        n.date,
        new Date(n.created_at).toLocaleString(),
        linkedShift?.date || '',
        n.text,
      ]
    })

    const filename = `kindredcare-evidence-notes-${new Date().toISOString().split('T')[0]}.csv`
    downloadCSV(filename, headers, rows)
    setToast({ message: `${filteredNotes.length} evidence notes exported as CSV`, type: 'success' })
  }

  // ═══════════════════════════════════════════════════════
  // PDF REPORT
  // ═══════════════════════════════════════════════════════

  async function exportPDF() {
    setGenerating(true)
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 40

      // ── Header bar ──
      doc.setFillColor(0, 84, 64) // #005440 teal dark
      doc.rect(0, 0, pageWidth, 90, 'F')

      // Logo mark
      doc.setFillColor(255, 255, 255)
      doc.roundedRect(margin, 25, 40, 40, 6, 6, 'F')
      doc.setTextColor(0, 84, 64)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(22)
      doc.text('K', margin + 20, 53, { align: 'center' })

      // Title
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(20)
      doc.text('KINDREDCARE', margin + 55, 45)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(11)
      doc.text('Care Documentation Report', margin + 55, 63)

      // Confidential tag
      doc.setFontSize(9)
      doc.text('CONFIDENTIAL', pageWidth - margin, 45, { align: 'right' })

      let y = 120

      // ── Metadata block ──
      doc.setTextColor(74, 85, 104) // gray
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)

      const metaLeft = margin
      const metaRight = pageWidth / 2 + 20

      doc.text('CAREGIVER', metaLeft, y)
      doc.text('REPORTING PERIOD', metaRight, y)
      y += 14
      doc.setTextColor(26, 32, 44) // ink
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text(profile?.name || 'Unknown', metaLeft, y)
      const periodText = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      doc.text(periodText, metaRight, y)

      y += 20
      doc.setTextColor(74, 85, 104)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.text('CARE RECIPIENT', metaLeft, y)
      doc.text('GENERATED', metaRight, y)
      y += 14
      doc.setTextColor(26, 32, 44)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      const recipientName = recipientFilter
        ? recipientMap[recipientFilter]?.name || 'Unknown'
        : 'All care recipients'
      doc.text(recipientName, metaLeft, y)
      doc.text(new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), metaRight, y)

      y += 30

      // ── Summary section ──
      doc.setDrawColor(226, 232, 240)
      doc.setLineWidth(0.5)
      doc.line(margin, y, pageWidth - margin, y)
      y += 20

      doc.setTextColor(0, 84, 64)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.text('Summary', margin, y)
      y += 18

      // Stat boxes (2x3 grid)
      const statBoxWidth = (pageWidth - 2 * margin - 20) / 3
      const statBoxHeight = 50
      const drawStat = (label, value, x, y) => {
        doc.setFillColor(247, 250, 252) // surface
        doc.roundedRect(x, y, statBoxWidth, statBoxHeight, 4, 4, 'F')
        doc.setTextColor(113, 128, 150) // gray-light
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.text(label.toUpperCase(), x + 10, y + 15)
        doc.setTextColor(0, 84, 64)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(16)
        doc.text(String(value), x + 10, y + 38)
      }

      drawStat('Logged shifts', summary.totalShifts, margin, y)
      drawStat('Logged hours', summary.totalHours.toFixed(1), margin + statBoxWidth + 10, y)
      drawStat('Direct care hours', summary.directCareHours.toFixed(1), margin + 2 * (statBoxWidth + 10), y)
      y += statBoxHeight + 10

      drawStat('No-shows covered', summary.noShows, margin, y)
      drawStat('Incidents flagged', summary.incidents, margin + statBoxWidth + 10, y)
      drawStat('Evidence notes', summary.notesCount, margin + 2 * (statBoxWidth + 10), y)
      y += statBoxHeight + 10

      // Observed shifts stat row (only if any exist)
      if (summary.observedShifts > 0) {
        // Amber-tinted stat cards for observed
        const drawObservedStat = (label, value, x, y) => {
          doc.setFillColor(252, 245, 232) // amber-light
          doc.roundedRect(x, y, statBoxWidth, statBoxHeight, 4, 4, 'F')
          doc.setTextColor(139, 80, 0) // amber
          doc.setFont('helvetica', 'normal')
          doc.setFontSize(8)
          doc.text(label.toUpperCase(), x + 10, y + 15)
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(16)
          doc.text(String(value), x + 10, y + 38)
        }
        drawObservedStat('Observed shifts', summary.observedShifts, margin, y)
        drawObservedStat('Observed hours', summary.observedHours.toFixed(1), margin + statBoxWidth + 10, y)
        y += statBoxHeight + 10
      }

      y += 15

      // Earnings section if configured
      if (rates && summary.earnings > 0) {
        doc.setFillColor(225, 245, 238) // teal light
        doc.roundedRect(margin, y, pageWidth - 2 * margin, 40, 6, 6, 'F')
        doc.setTextColor(0, 84, 64)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.text('DOCUMENTED EARNINGS', margin + 12, y + 15)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(16)
        doc.text(`$${summary.earnings.toFixed(2)}`, margin + 12, y + 33)
        y += 55
      }

      // ── Record strength / confidence breakdown ──
      // Only meaningful if there are backfilled (reconstructed) shifts in range.
      const cb = summary.confidenceBreakdown
      if (cb.total > 0) {
        if (y > pageHeight - 160) { doc.addPage(); y = margin }

        doc.setTextColor(0, 84, 64)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(13)
        doc.text('Record strength', margin, y)
        y += 6
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(8)
        doc.setTextColor(113, 128, 150)
        doc.text(
          'How this record characterizes its own entries. Real-time entries carry their own timestamp; reconstructed entries are graded by their supporting basis.',
          margin, y + 8, { maxWidth: pageWidth - 2 * margin }
        )
        y += 22

        const strengthRows = []
        if (summary.realtimeCount > 0) {
          strengthRows.push(['Real-time entries', String(summary.realtimeCount), 'Logged live; timestamp is the proof'])
        }
        if (cb.documented > 0) {
          strengthRows.push(['Documented', String(cb.documented), 'Backed by a record (pay stub, bank, text, EVV, calendar)'])
        }
        if (cb.pattern > 0) {
          strengthRows.push(['Pattern-based', String(cb.pattern), 'Follows a known regular schedule'])
        }
        if (cb.recollection > 0) {
          strengthRows.push(['From memory', String(cb.recollection), 'Reconstructed from recollection; no supporting record'])
        }

        autoTable(doc, {
          startY: y,
          head: [['Basis', 'Shifts', 'What this means']],
          body: strengthRows,
          theme: 'grid',
          headStyles: { fillColor: [45, 55, 72], textColor: 255, fontSize: 9, fontStyle: 'bold' },
          bodyStyles: { fontSize: 8, textColor: [74, 85, 104] },
          alternateRowStyles: { fillColor: [247, 250, 252] },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 0) {
              const match = Object.values(CONFIDENCE_PDF).find(c => c.label === data.cell.raw)
              if (match) {
                data.cell.styles.textColor = match.rgb
                data.cell.styles.fontStyle = 'bold'
              }
            }
          },
        })
        y = doc.lastAutoTable.finalY + 25
      }

      // ── Shifts tables (logged and observed separately) ──
      const loggedShifts = filteredShifts.filter(s => s.authorship !== 'observed')
      const observedShifts = filteredShifts.filter(s => s.authorship === 'observed')

      const renderShiftsTable = (title, subtitle, shiftList) => {
        if (shiftList.length === 0) return

        if (y > pageHeight - 200) { doc.addPage(); y = margin }

        doc.setTextColor(0, 84, 64)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(13)
        doc.text(title, margin, y)
        if (subtitle) {
          y += 4
          doc.setFont('helvetica', 'italic')
          doc.setFontSize(8)
          doc.setTextColor(113, 128, 150)
          doc.text(subtitle, margin, y + 8)
          y += 12
        }
        y += 8

        const isObserved = shiftList === observedShifts
        const shiftRows = shiftList.map(s => {
          const hours = ((new Date(s.clock_out) - new Date(s.clock_in)) / 3600000).toFixed(1)
          const corrob = corroborations[s.id]?.[0]
          const flags = [
            s.incident ? '⚠' : '',
            s.is_backfilled ? 'B' : '',
          ].filter(Boolean).join(' ')

          // Confidence label for backfilled shifts; blank for real-time (its timestamp is proof)
          const confidenceCell = s.is_backfilled
            ? (CONFIDENCE_PDF[s.confidence]?.label || 'From memory')
            : '—'

          if (isObserved) {
            return [
              new Date(s.clock_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              `${new Date(s.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} – ${new Date(s.clock_out).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
              `${hours}h`,
              s.observed_caregiver_name || '',
              (s.evidence_source || '').replace(/_/g, ' '),
              confidenceCell,
              flags,
            ]
          }

          return [
            new Date(s.clock_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            `${new Date(s.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} – ${new Date(s.clock_out).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
            `${hours}h`,
            s.shift_type.replace(/_/g, ' '),
            s.recipient_id ? (recipientMap[s.recipient_id]?.name || '') : '',
            confidenceCell,
            flags,
            corrob ? `${corrob.state} by ${corrob.corroborator_name}` : '',
          ]
        })

        const head = isObserved
          ? [['Date', 'Time', 'Duration', 'Worked by', 'Source', 'Confidence', 'Flag']]
          : [['Date', 'Time', 'Duration', 'Type', 'Recipient', 'Confidence', 'Flag', 'Corroboration']]

        // Column index of the Confidence cell differs per table
        const confidenceCol = isObserved ? 5 : 5

        autoTable(doc, {
          startY: y + 5,
          head,
          body: shiftRows,
          theme: 'grid',
          headStyles: {
            fillColor: isObserved ? [139, 80, 0] : [0, 84, 64],
            textColor: 255, fontSize: 9, fontStyle: 'bold'
          },
          bodyStyles: { fontSize: 8, textColor: [74, 85, 104] },
          alternateRowStyles: { fillColor: [247, 250, 252] },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            // Color-code the confidence cell so a reviewer can see tiers at a glance
            if (data.section === 'body' && data.column.index === confidenceCol) {
              const raw = data.cell.raw
              const match = Object.values(CONFIDENCE_PDF).find(c => c.label === raw)
              if (match) {
                data.cell.styles.textColor = match.rgb
                data.cell.styles.fontStyle = 'bold'
              }
            }
          },
        })

        y = doc.lastAutoTable.finalY + 25
      }

      renderShiftsTable(
        'Logged shifts',
        loggedShifts.length > 0 ? 'Shifts worked directly by the caregiver named above.' : null,
        loggedShifts
      )
      renderShiftsTable(
        'Observed shifts',
        observedShifts.length > 0 ? 'Shifts worked by other caregivers, documented from the family caregiver perspective.' : null,
        observedShifts
      )

      // ── Evidence notes ──
      if (filteredNotes.length > 0) {
        if (y > pageHeight - 150) { doc.addPage(); y = margin }

        doc.setTextColor(0, 84, 64)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(13)
        doc.text('Evidence notes', margin, y)
        y += 5
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(8)
        doc.setTextColor(113, 128, 150)
        doc.text('All notes are timestamped and tamper-evident. They cannot be edited or deleted.', margin, y + 10)
        y += 25

        for (const note of filteredNotes) {
          if (y > pageHeight - 100) { doc.addPage(); y = margin }

          doc.setFillColor(247, 250, 252)
          doc.roundedRect(margin, y - 5, pageWidth - 2 * margin, 12, 3, 3, 'F')
          doc.setTextColor(0, 84, 64)
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(9)
          doc.text(formatDate(note.date), margin + 8, y + 3)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(113, 128, 150)
          doc.setFontSize(8)
          doc.text(`Recorded ${new Date(note.created_at).toLocaleString()}`, pageWidth - margin - 8, y + 3, { align: 'right' })
          y += 15

          doc.setTextColor(26, 32, 44)
          doc.setFont('helvetica', 'normal')
          doc.setFontSize(10)
          const wrappedText = doc.splitTextToSize(note.text, pageWidth - 2 * margin - 16)
          doc.text(wrappedText, margin + 8, y + 8)
          y += wrappedText.length * 12 + 15
        }
      }

      // ── Footer on every page ──
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setDrawColor(226, 232, 240)
        doc.line(margin, pageHeight - 50, pageWidth - margin, pageHeight - 50)
        doc.setTextColor(113, 128, 150)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.text(
          'Generated by KindredCare · Tamper-evident caregiver documentation · kindredcare.io',
          margin,
          pageHeight - 30
        )
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 30, { align: 'right' })
      }

      const filename = `kindredcare-report-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(filename)
      setToast({ message: 'PDF report downloaded', type: 'success' })
    } catch (err) {
      setToast({ message: `Error generating PDF: ${err.message}`, type: 'error' })
    } finally {
      setGenerating(false)
    }
  }

  const hasData = filteredShifts.length > 0 || filteredNotes.length > 0

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <PageHeader
        title="Export"
        description="Download your care records as CSV or a formatted PDF report — for personal files, case managers, attorneys, or the Department of Labor."
      />

      {/* ── Range selector ── */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Filter size={16} style={{ color: '#2C7A7B' }} />
          <h2 style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C' }}>Filter records</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
          <div>
            <label style={labelStyle}>Date range</label>
            <select value={rangePreset} onChange={e => setRangePreset(e.target.value)} style={inputStyle}>
              {RANGE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          {recipients.length > 0 && (
            <div>
              <label style={labelStyle}>Care recipient</label>
              <select value={recipientFilter} onChange={e => setRecipientFilter(e.target.value)} style={inputStyle}>
                <option value="">All care recipients</option>
                {recipients.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {rangePreset === 'custom' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Start date</label>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>End date</label>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={inputStyle} />
            </div>
          </div>
        )}

        <div style={{ marginTop: '12px', padding: '10px 14px', background: '#E1F5EE', borderRadius: '8px', fontSize: '13px', color: '#005440', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Calendar size={14} />
          Reporting period: <strong>{start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – {end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
        </div>
      </Card>

      {/* ── Preview / Summary ── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
          Preview
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          <StatCard label="Logged shifts" value={summary.totalShifts} />
          <StatCard label="Logged hours" value={summary.totalHours.toFixed(1)} />
          {summary.observedShifts > 0 && (
            <StatCard label="Observed shifts" value={summary.observedShifts} sub={`${summary.observedHours.toFixed(1)}h witnessed`} color="#8b5000" />
          )}
          <StatCard label="Notes" value={summary.notesCount} />
          {summary.earnings > 0 && <StatCard label="Earnings" value={`$${summary.earnings.toFixed(2)}`} color="#005440" />}
          {summary.incidents > 0 && <StatCard label="Incidents" value={summary.incidents} color="#E07856" />}
        </div>
      </div>

      {/* ── Export options ── */}
      <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
        Download
      </h2>

      {!hasData ? (
        <Card style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
          <FileText size={32} style={{ color: '#E2E8F0', marginBottom: '12px' }} />
          <div style={{ fontSize: '14px' }}>No records in the selected range. Try a wider date range or removing filters.</div>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          <ExportOption
            icon={FileText}
            title="Formatted PDF report"
            description={`A branded, print-ready report with summary stats, complete shift log, and evidence notes. Suitable for handing to a case manager, attorney, or the Department of Labor.`}
            action={<Button onClick={exportPDF} disabled={generating}><Download size={16} /> {generating ? 'Generating...' : 'Download PDF'}</Button>}
            highlighted
          />
          <ExportOption
            icon={FileSpreadsheet}
            title="Shifts (CSV)"
            description={`Every shift in the selected range with times, tasks, notes, corroborations, and tamper-evident status. Opens in Excel, Google Sheets, or Numbers.`}
            action={<Button variant="secondary" onClick={exportShiftsCSV} disabled={filteredShifts.length === 0}>
              <Download size={16} /> Download CSV ({filteredShifts.length})
            </Button>}
          />
          <ExportOption
            icon={FileSpreadsheet}
            title="Evidence notes (CSV)"
            description={`All timestamped evidence notes in the selected range, with linked shift dates where applicable.`}
            action={<Button variant="secondary" onClick={exportNotesCSV} disabled={filteredNotes.length === 0}>
              <Download size={16} /> Download CSV ({filteredNotes.length})
            </Button>}
          />
        </div>
      )}

      {/* Compliance note */}
      <Card style={{ marginTop: '1.5rem', background: '#F7FAFC', borderLeft: '3px solid #2C7A7B' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <Shield size={16} style={{ color: '#005440', marginTop: '2px', flexShrink: 0 }} />
          <div style={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.6 }}>
            <strong style={{ color: '#1A202C' }}>About these exports.</strong> Exported records preserve the tamper-evident properties of the original: timestamps, lock status, and corroboration history are all included. Once a shift is locked (30 minutes after clock-out), it cannot be edited even in the original record. The PDF report is designed to be evidentiary — it can be printed, attached to case files, or submitted with wage complaints.
          </div>
        </div>
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}

function ExportOption({ icon: Icon, title, description, action, highlighted }) {
  return (
    <Card style={{
      display: 'flex',
      gap: '14px',
      alignItems: 'flex-start',
      background: highlighted ? '#E1F5EE' : undefined,
      borderColor: highlighted ? '#2C7A7B' : undefined,
    }}>
      <div style={{
        padding: '10px',
        background: highlighted ? '#FFFFFF' : '#F7FAFC',
        borderRadius: '8px',
        flexShrink: 0,
      }}>
        <Icon size={20} style={{ color: highlighted ? '#005440' : '#4A5568' }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>{title}</div>
        <div style={{ fontSize: '13px', color: '#4A5568', marginBottom: '10px', lineHeight: 1.5 }}>{description}</div>
        {action}
      </div>
    </Card>
  )
}

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }
const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', background: '#FFFFFF' }
